import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import Empty from 'constants/empty';
import FrameworkUtils from 'utils/framework';
import { p13nSelector } from 'selectors/p13n/p13nSelector';
import { coreUserDataSelector } from 'viewModel/selectors/user/coreUserDataSelector';
import cookieUtils from 'utils/Cookies';
import PersonalizationUtils from 'utils/Personalization';
import P13nUtils from 'utils/localStorage/P13n';
import { sendCmsComponentEvent } from 'analytics/utils/cmsComponents';
import PersonalizedPlacementActions from 'actions/PersonalizedPlacementActions';

import constants from 'constants/content';
import anaConsts from 'analytics/constants';

const {
    BANNER_TYPES: { PERSISTENT, DEFAULT }
} = constants;

const {
    COMPONENT_TYPES: { BANNER }
} = constants;

const {
    CMS_COMPONENT_EVENTS: { IMPRESSION, ITEM_CLICK }
} = anaConsts;

const triggerImpression = function (banner) {
    const eventName = IMPRESSION;
    const bannerType = banner?.variationData?.bannerType || banner?.bannerType;
    const component = bannerType === PERSISTENT ? PERSISTENT : BANNER;

    sendCmsComponentEvent({
        items: [banner],
        eventName,
        sid: '',
        component,
        eventOriginComponent: 'withBannerProps'
    });
};

const triggerClick = async function (banner) {
    const { sid: componentSid, variationData } = banner;

    const eventName = ITEM_CLICK;

    const bannerType = banner?.variationData?.bannerType || banner?.bannerType;
    const component = bannerType !== DEFAULT ? PERSISTENT : BANNER;

    await sendCmsComponentEvent({
        items: [variationData],
        eventName,
        sid: '',
        componentSid,
        component
    });
};

const { getPersonalizedComponent, checkDataToDisplay } = PersonalizationUtils;
const { setPersonalizationCache } = P13nUtils;
const { wrapHOC } = FrameworkUtils;
const { setOriginalBannerSid } = PersonalizedPlacementActions;

const getPersonalizedData = (prvCookie, p13n, personalization, user, activeVariation, nonTargettedVariations) => {
    let personalizedComponent = Empty.Array;
    let activePage = -1;
    let bannerHasRequestedPersonalization = false;

    if (prvCookie && p13n.variations && p13n.activeVariations && activeVariation) {
        const variation = p13n.variations?.[personalization?.context]?.find(item => item?.sys?.id === activeVariation);

        if (variation) {
            personalizedComponent = {
                variationData: variation
            };
            activePage = p13n.variations?.[personalization?.context]?.findIndex(item => item?.sys?.id === activeVariation);
            activePage = activePage + (nonTargettedVariations?.length || 0);
        }
    } else if (prvCookie && p13n.data?.length > 0) {
        personalizedComponent = p13n.data.find(item => item.context === personalization?.context) || Empty.Array;
    } else if (p13n.headData?.length) {
        setPersonalizationCache(p13n.headData);
        const headItemData = p13n.headData.find(item => (item.p13n?.context || item.context) === personalization?.context);
        personalizedComponent = checkDataToDisplay(headItemData, personalization);
    } else if (user.isAnonymous) {
        personalizedComponent = Empty.Array;
    } else {
        bannerHasRequestedPersonalization = true;
        personalizedComponent = getPersonalizedComponent(personalization, user, p13n, true);
    }

    return { personalizedComponent, activePage, bannerHasRequestedPersonalization };
};

const fields = createSelector(
    p13nSelector,
    coreUserDataSelector,
    (_state, ownProps) => ownProps.personalization,
    (_state, ownProps) => ownProps.nonTargettedVariations,
    (_state, ownProps) => ownProps.nonTargettedPreviewIndex,
    (_state, ownProps) => ownProps.isActiveBanner,
    (_state, ownProps) => ownProps.bannerType,
    (p13n, user, personalization, nonTargettedVariations, nonTargettedPreviewIndex, isActiveBanner, bannerType) => {
        let personalizedComponent = Empty.Array;
        let activePage = -1;
        let bannerHasRequestedPersonalization = false;
        let calculatedActivePage = -1;
        const isPersistentBanner = bannerType === 'PersistentBanner';

        const hasOriginalBanner = p13n.originalBannerSid != null && p13n.originalBannerSid !== '';

        if (!Sephora.isNodeRender && Sephora.Util.InflatorComps.services.loadEvents.HydrationFinished) {
            const prvCookie = cookieUtils.read(cookieUtils.KEYS.P13N_PRV);
            const activeVariation = p13n.activeVariations?.[personalization?.context];
            const isPersistentBannerWithNBO = personalization?.isNBOEnabled;

            // Skip personalization if:
            // 1. It's a persistent banner without NBO and not the active banner
            // 2. It's a non-persistent banner and not the active banner (when there's an active selection)
            const shouldSkipPersonalization =
                (isPersistentBanner && hasOriginalBanner && !isActiveBanner && !isPersistentBannerWithNBO) ||
                (!isPersistentBanner && hasOriginalBanner && !isActiveBanner);

            // Get personalized data unless we should skip it
            if (!shouldSkipPersonalization) {
                const result = getPersonalizedData(prvCookie, p13n, personalization, user, activeVariation, nonTargettedVariations);
                personalizedComponent = result.personalizedComponent;
                activePage = result.activePage;
                bannerHasRequestedPersonalization = result.bannerHasRequestedPersonalization;
            }

            // Calculate activePage based on banner type
            if (isPersistentBanner) {
                // For persistent banners
                calculatedActivePage = activePage + 1;
            } else {
                // For non-persistent banners
                let finalActivePage = activePage;

                if (isActiveBanner && nonTargettedPreviewIndex > -1) {
                    finalActivePage = nonTargettedPreviewIndex;
                }

                calculatedActivePage = isActiveBanner && nonTargettedPreviewIndex > -1 ? finalActivePage : isActiveBanner ? finalActivePage + 1 : -1;
            }
        }

        return {
            p13n,
            user,
            personalizedComponent,
            triggerImpression,
            triggerClick,
            activePage: calculatedActivePage,
            bannerHasRequestedPersonalization
        };
    }
);

const functions = {
    setOriginalBannerSid
};

const withBannerProps = wrapHOC(connect(fields, functions));

export {
    withBannerProps, fields
};
